import * as angular from 'angular';

import { LogLevel, IApiInformation, IApiConfiguration, IUiConfiguration, JsonConfiguration } from '../../models';
import { ILoggerService, ISignalrService, IRequestService, RequestService } from '../../services';
import { KeycloakConfig } from 'keycloakConfig';

export interface IConfigurationObjectAdmin {
    email: string;
}

export interface ISupportDetails {
    content: string[];
    email: string;
}

export interface ICustomHelp {
    icon: string;
    tooltip: string;
    url: string;
}

export interface IConfigurationObject {
    admin: IConfigurationObjectAdmin;
    apiUrl: string;
    azure?: IAzureConfiguration;
    backgroundUrl?: string;
    customHelp?: ICustomHelp;
    debugBadge?: boolean;
    friendlyName?: string;
    hideApiInfo?: boolean;
    keycloakEnabled: boolean;
    logo?: string;
    supportDetails?: ISupportDetails;
    useExperimentalFeatures?: boolean;
}

export interface IContextualDocEntry {
    description: string;
    icon?: string;
    isEditMode: boolean;
    tooltip?: string;
    url: string;
}

export interface IContextualDocConfiguration {
    [stateName: string]: {
        links: IContextualDocEntry[];
        comment?: string;
        icon?: string;
        parent?: string;
    };
}

export interface IAzureConfiguration {
    authority: string;
    clientId: string;
    redirectUri: string;
}


export interface IUiInformation {
    date: Date;
    version: string;
}

export interface IConfigurationService {
    configuration: IConfigurationObject;
    getApiBuildDate: () => angular.IPromise<Date>;
    getApiConfiguration(): angular.IPromise<IApiConfiguration>;
    getApiInformation(): angular.IPromise<IApiInformation>;
    getBuildDate: () => angular.IPromise<Date>;
    getConfiguration: () => angular.IPromise<IConfigurationObject>;
    getContextualDocConfiguration(): angular.IPromise<IContextualDocConfiguration>;
    getJsonConfiguration(): angular.IPromise<JsonConfiguration>;
    getKeycloakConfig(): angular.IPromise<KeycloakConfig>;
    getLogLevels(): angular.IPromise<{ apiLevel: LogLevel; serviceLevel: LogLevel; enableExecutionTimeLogging: boolean }>;
    getLogoFileName: () => angular.IPromise<string>;
    getUiConfiguration(): angular.IPromise<IUiConfiguration>;
    getUiInformation(): angular.IPromise<IUiInformation>;
    loadData: () => void;
    setApiConfiguration(configuration: IApiConfiguration): angular.IPromise<IApiConfiguration>;
    setJsonConfiguration(configuration: JsonConfiguration): angular.IPromise<JsonConfiguration>;
    setLogLevels(apiLevel: LogLevel, serviceLevel: LogLevel, enableExecutionTimeLogging: boolean): angular.IPromise<LogLevel>;
    setUiConfiguration(configuration: IUiConfiguration): angular.IPromise<IUiConfiguration>;
}

export class ConfigurationService implements IConfigurationService {
    static serviceId = 'configurationService';

    configuration: IConfigurationObject;

    static $inject = [
        '$http',
        '$q',
        'loggerService',
        'requestService',
        'signalrService',
        'wtConfig'
    ];

    constructor(
        private _$http: angular.IHttpService,
        private _$q: angular.IQService,
        private _loggerService: ILoggerService,
        private _requestService: IRequestService,
        private _signalrService: ISignalrService,
        private _wtConfig
    ) {
    }

    loadData(): void {
        if (this._wtConfig && this._wtConfig.apiUrl) {
            this.configuration = this._wtConfig;
            const apiUrl = this._wtConfig.apiUrl;
            RequestService.setBaseUrl(apiUrl);
            this._signalrService.setBaseUrl(apiUrl);
        }
    }

    getConfiguration(): angular.IPromise<IConfigurationObject> {
        const defered = this._$q.defer<IConfigurationObject>();

        this._$http
            .get('config.json')
            .then((data: any) => {
                const apiUrl = data.data.apiUrl;
                RequestService.setBaseUrl(apiUrl);
                this._signalrService.setBaseUrl(apiUrl);
                defered.resolve(data.data);
            })
            .catch(reason => {
                this._loggerService.logError(reason);
                defered.reject(reason);
            });

        return defered.promise;
    }

    getUiInformation(): angular.IPromise<IUiInformation> {
        return this._$http
            .get('buildDate.json', { headers: { 'Cache-Control': 'no-cache' } })
            .then((data: any) => {
                const result = data.data;
                result.date = new Date(result.date);
                return result;
            });
    }

    getBuildDate(): angular.IPromise<Date> {
        const defered = this._$q.defer<Date>();

        this._$http
            .get('buildDate.json', { headers: { 'Cache-Control': 'no-cache' } })
            .then((data: any) => {
                const date = new Date(data.data.date);
                defered.resolve(date);
            })
            .catch(reason => {
                this._loggerService.logError(reason);
                defered.reject(reason);
            });

        return defered.promise;
    }

    getApiBuildDate(): angular.IPromise<Date> {
        const defered = this._$q.defer<Date>();

        if (this.configuration) {
            const url = this.configuration.apiUrl + 'api/v1/ApiInformation';
            this._requestService
                .get(url)
                .then((data: any) => {
                    defered.resolve(new Date(data.data.buildDate));
                })
                .catch(reason => {
                    this._loggerService.logError(reason, false);
                    defered.reject(reason);
                });
        } else {
            this._loggerService.logError('configuration not loaded.', false);
        }

        return defered.promise;
    }

    getApiInformation(): angular.IPromise<IApiInformation> {
        const defered = this._$q.defer<IApiInformation>();

        if (this.configuration) {
            const url = this.configuration.apiUrl + 'api/v1/ApiInformation';
            this._requestService
                .get(url)
                .then(data => defered.resolve(data))
                .catch(reason => {
                    this._loggerService.logError(reason, false);
                    defered.reject(reason);
                });
        } else {
            this._loggerService.logError('configuration not loaded.', false);
            defered.reject();
        }

        return defered.promise;
    }

    getKeycloakConfig(): angular.IPromise<KeycloakConfig> {
        return this._requestService.getAtV1('ApiInformation/authenticationInformation')
        .then((data: any) => {
            return {
                clientId: data.keycloakClientIds.keycloak_client_id_angular,
                url: data.keycloakRealmBaseUrl,
                realm: data.keycloakRealmName
            };
        });
    }

    getLogoFileName(): angular.IPromise<string> {
        const defered = this._$q.defer<string>();
        const defaultLogo = 'logo_blue.svg';
        this.getConfiguration()
            .then(data => {
                const logoFileName = data.logo ? data.logo : defaultLogo;
                defered.resolve(logoFileName);
            })
            .catch(() => defered.resolve(defaultLogo));
        return defered.promise;
    }

    getLogLevels(): angular.IPromise<{ apiLevel: LogLevel, serviceLevel: LogLevel, enableExecutionTimeLogging: boolean }> {
        const url = 'configuration/logLevels';
        return this._requestService
            .getAtV1<{ apiLevel: LogLevel, serviceLevel: LogLevel, enableExecutionTimeLogging: boolean }>(url)
            .then(levels => {
                this._requestService.cleanCacheAtV1(url);
                return levels;
            });
    }

    getContextualDocConfiguration(): angular.IPromise<IContextualDocConfiguration> {
        const url = 'content/documentation/contextual.json';
        return this._$http
            .get<IContextualDocConfiguration>(url)
            .then(req => { return req.data; });
    }

    setLogLevels(apiLevel: LogLevel, serviceLevel: LogLevel, enableExecutionTimeLogging: boolean): angular.IPromise<LogLevel> {
        return this._requestService
            .postAtV1('configuration/logLevels', { apiLevel: apiLevel, serviceLevel: serviceLevel, enableExecutionTimeLogging: enableExecutionTimeLogging });
    }

    getApiConfiguration(): angular.IPromise<IApiConfiguration> {
        return this._requestService.getAtV1('configuration/api');
    }

    getJsonConfiguration(): angular.IPromise<JsonConfiguration> {
        return this._requestService.getAtV1('configuration/json');
    }

    getUiConfiguration(): angular.IPromise<IUiConfiguration> {
        return this._requestService.getAtV1('configuration/ui');
    }

    setApiConfiguration(configuration: IApiConfiguration): angular.IPromise<IApiConfiguration> {
        return this._requestService.postAtV1('configuration/api', configuration);
    }

    setJsonConfiguration(configuration: JsonConfiguration): angular.IPromise<JsonConfiguration> {
        return this._requestService.postAtV1('configuration/json', configuration);
    }

    setUiConfiguration(configuration: IUiConfiguration): angular.IPromise<IUiConfiguration> {
        return this._requestService.postAtV1('configuration/ui', configuration);
    }
}